import { LOCIZE_NS } from '@avanta/shared/constants/common';
import { AppointmentService } from '@avanta/shared/services/appointments';
import { useMutation, type UseMutationResult, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { toast } from 'sonner';
import { MUTATION_KEYS, QUERY_KEYS } from 'constants/common';
import { ROUTES } from 'constants/routes';
import { useUserStore } from 'stores/user';
import { useComponentTracking } from 'tracking/hooks/useTrackingComponent';
import { type AppointmentPayload } from 'types/appointments';

export const useBookAppointment = (): UseMutationResult<void, Error, AppointmentPayload> => {
  const isPsychologist = useUserStore(state => state.isPsychologist);
  const queryClient = useQueryClient();
  const { trackAppointmentConfirmed } = useComponentTracking();
  const navigate = useNavigate();
  const { t } = useTranslation(LOCIZE_NS.APPOINTMENTS);

  const getRedirectRoute = (): string => {
    if (isPsychologist) return ROUTES.PSYCHOLOGIST_PATIENTS;
    return ROUTES.APPOINTMENTS;
  };

  const commonQueriesToInvalidate = [
    QUERY_KEYS.FETCH_NEXT_APPOINTMENTS,
    QUERY_KEYS.FETCH_PSYCHOLOGIST_AVAILABILITY,
    QUERY_KEYS.FETCH_COUPONS,
    QUERY_KEYS.FETCH_PATIENT,
  ];

  return useMutation<void, Error, AppointmentPayload>({
    mutationKey: [MUTATION_KEYS.BOOK_APPOINTMENT],
    mutationFn: async (appointment: AppointmentPayload) => {
      await AppointmentService.book(appointment);
    },
    onSuccess: () => {
      commonQueriesToInvalidate.forEach(key => queryClient.invalidateQueries({ queryKey: [key] }));
      toast.success(t('AppointmentsReserve.success'), { id: 'book-appointment-success' });
      void trackAppointmentConfirmed();
      if (isPsychologist) {
        queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.FETCH_PSYCHOLOGIST_NEXT_SESSIONS] });
      }
      navigate(getRedirectRoute());
    },
    onError: () => {
      toast.error(t('AppointmentsReserve.error'), { id: 'book-appointment-error' });
    },
  });
};
